/**
 * 1.3.0 portal: New upload (users with an upload rule). The rule's metadata form and the rights declaration, then
 * one or more files: each file its own submission, or one submission for the batch when the rule groups files.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { t } from '../i18n/en';
import { errorText, portalApi } from '../client';
import { formatBytes } from '../format';
import { DropZone, FieldsForm, Loading, OpenResumeCards, RightsBox, UploadProgress, missingField, useLoad } from '../components';
import { startUpload, type PortalUpload } from '../uploader';
import type { PortalSubmission, PortalUploadRule } from '../types';

const baseName = (f: File) => f.name.replace(/\.[^.]+$/, '');

export function NewUploadPage() {
  const { data: rule, error, reload } = useLoad<PortalUploadRule>('/upload-rule');
  const [values, setValues] = useState<Record<string, string>>({});
  const [rights, setRights] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [running, setRunning] = useState<PortalUpload[]>([]);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [, force] = useState(0);
  if (!rule) {
    if (error && (error as { status?: number }).status === 404) return <p className="p-empty">{t('portal.open.notAllowed')}</p>;
    return <Loading error={error} retry={reload} />;
  }

  const start = async () => {
    const miss = missingField(rule.supplierFields, values);
    if (miss) { setMsg({ ok: false, text: t('portal.fieldRequired', { field: miss.label }) }); return; }
    if (!rights) { setMsg({ ok: false, text: t('error.RIGHTS_REQUIRED') }); return; }
    const big = files.find((f) => f.size > rule.maxBytes);
    if (big) { setMsg({ ok: false, text: `${big.name}: ${t('portal.error.UPLOAD_TOO_LARGE')} (${formatBytes(rule.maxBytes)})` }); return; }
    setBusy(true);
    setMsg(null);
    try {
      const create = (title: string | undefined) => portalApi<PortalSubmission>('/submissions', {
        method: 'POST', json: { metadata: values, rightsAccepted: true, rightsVersion: rule.rights.version, ...(title ? { title } : {}) },
      });
      const started: PortalUpload[] = [];
      const done = () => force((n) => n + 1);
      if (rule.groupFiles) {
        const sub = await create(values.f_title?.trim() || (files.length === 1 ? baseName(files[0]!) : undefined));
        files.forEach((f, i) => started.push(startUpload(`open:${sub.id}:${i}`, f, { submissionId: sub.id }, { onDone: done })));
      } else {
        for (const [i, f] of files.entries()) {
          const title = files.length === 1 ? values.f_title?.trim() || baseName(f) : baseName(f);
          const sub = await create(title);
          started.push(startUpload(`open:${sub.id}:${i}`, f, { submissionId: sub.id }, { onDone: done }));
        }
      }
      for (const u of started) u.subscribe(done);
      setRunning(started);
      setFiles([]);
      setMsg({ ok: true, text: t('portal.open.sent') });
    } catch (err) {
      setMsg({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="p-page-head">
        <h1>{t('portal.open.title')}</h1>
        <p className="p-muted">{rule.groupFiles ? t('portal.open.introGrouped') : t('portal.open.intro')}</p>
      </div>
      {!rule.scanned ? <p className="p-notice p-notice-warn">{t('portal.upload.notScanned')}</p> : null}
      <OpenResumeCards />
      {running.length ? (
        <section className="p-card">
          {running.map((u) => <UploadProgress key={u.key} upload={u} />)}
          <div className="p-row">
            <Link to="/uploads" className="p-btn">{t('portal.myUploads')}</Link>
            {running.every((u) => !u.active) ? <button type="button" className="p-btn p-btn-quiet" onClick={() => setRunning([])}>{t('portal.open.another')}</button> : null}
          </div>
        </section>
      ) : null}
      <section className="p-card" data-testid="new-upload">
        <h3>{t('portal.open.details')}</h3>
        <FieldsForm fields={rule.supplierFields} values={values} onChange={setValues} />
        <DropZone
          multiple media acceptedTypes={rule.acceptedTypes}
          onFiles={(list) => setFiles((cur) => [...cur, ...list])}
        />
        {files.length ? (
          <ul className="p-files">
            {files.map((f, i) => (
              <li key={`${f.name}-${i}`} className="p-row p-between">
                <span><span className="p-file">{f.name}</span> <span className="p-muted p-small">{formatBytes(f.size)}</span></span>
                <button type="button" className="p-btn p-btn-quiet p-btn-small" onClick={() => setFiles(files.filter((_, j) => j !== i))}>{t('common.cancel')}</button>
              </li>
            ))}
          </ul>
        ) : null}
        <RightsBox text={rule.rights.text} checked={rights} onChange={setRights} />
        <div className="p-row">
          <button type="button" className="p-btn p-btn-primary" disabled={busy || files.length === 0} onClick={() => void start()} data-testid="start-upload">{t('portal.open.start')}</button>
        </div>
        {msg ? <p className={msg.ok ? 'p-good' : 'p-bad'} role="status">{msg.text}</p> : null}
      </section>
    </>
  );
}
